/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { STORAGE_KEYS } from '../lib/state/appState';
import { defaultSiteMode, normalizeSiteModeConfig, readSiteModeConfig, type SiteModeConfig } from '../lib/siteMode';

type SiteModeContextValue = {
  config: SiteModeConfig;
  maintenanceEnabled: boolean;
  saveConfig: (updates: Partial<SiteModeConfig>) => void;
};

const SITE_MODE_EVENT = 'zobu:site-mode-changed';

const SiteModeContext = createContext<SiteModeContextValue | null>(null);

function loadConfig() {
  if (typeof window === 'undefined') {
    return defaultSiteMode;
  }

  return readSiteModeConfig(STORAGE_KEYS.siteMode);
}

export function SiteModeProvider({ children }: PropsWithChildren) {
  const [config, setConfig] = useState<SiteModeConfig>(loadConfig);

  useEffect(() => {
    const syncFromStorage = () => setConfig(loadConfig());

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === STORAGE_KEYS.siteMode) {
        syncFromStorage();
      }
    };

    const handleCustomEvent = () => {
      syncFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(SITE_MODE_EVENT, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(SITE_MODE_EVENT, handleCustomEvent);
    };
  }, []);

  const value = useMemo<SiteModeContextValue>(
    () => ({
      config,
      maintenanceEnabled: config.maintenanceEnabled,
      saveConfig: (updates) => {
        const next = normalizeSiteModeConfig({
          ...config,
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        window.localStorage.setItem(STORAGE_KEYS.siteMode, JSON.stringify(next));
        window.dispatchEvent(new Event(SITE_MODE_EVENT));
        setConfig(next);
      },
    }),
    [config],
  );

  return <SiteModeContext.Provider value={value}>{children}</SiteModeContext.Provider>;
}

export function useSiteMode() {
  const context = useContext(SiteModeContext);
  if (!context) {
    throw new Error('useSiteMode must be used within SiteModeProvider');
  }
  return context;
}
